import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';

export type ListResponse<T> = { data: T[]; total?: number };

async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API ${path} → ${res.status}`);
  return res.json();
}

async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`API ${path} → ${res.status}`);
  return res.json();
}

import { supabase } from './supabase';
async function withAuth(init: RequestInit): Promise<RequestInit> {
  const { data } = await supabase.auth.getSession();
  const headers = new Headers(init.headers);
  if (data.session) headers.set('Authorization', `Bearer ${data.session.access_token}`);
  return { ...init, headers };
}

async function fetchWithAuth<T>(path: string, init: RequestInit): Promise<T> {
  const authedInit = await withAuth(init);
  const res = await fetch(`/api${path}`, authedInit);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${path} → ${res.status}: ${body}`);
  }
  return res.json();
}

export const api = {
  get: <T>(path: string) => apiGet<T>(path),
  post: <T>(path: string, body: unknown) => apiPost<T>(path, body),
  put: <T>(path: string, body: unknown) => fetchWithAuth<T>(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }),
  patch: <T>(path: string, body: unknown) => fetchWithAuth<T>(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }),
  delete: <T>(path: string) => fetchWithAuth<T>(path, { method: 'DELETE' }),
};
// silence unused
void apiPut; void apiDelete;

export function useEntityList<T>(endpoint: string) {
  return useQuery({
    queryKey: ['list', endpoint],
    queryFn: () => api.get<ListResponse<T>>(endpoint),
  });
}

export function useEntity<T>(endpoint: string, id: string | null | undefined) {
  return useQuery({
    queryKey: ['entity', endpoint, id],
    queryFn: () => api.get<T>(`${endpoint}/${id}`),
    enabled: !!id,
  });
}

/**
 * Invalida la cache del listado del endpoint base y, opcionalmente, de
 * un endpoint enriquecido alternativo (típico cuando el listado se carga
 * desde /listado/full y el CRUD opera contra el base).
 */
function invalidateLists(
  qc: ReturnType<typeof useQueryClient>,
  endpoint: string,
  extra?: string,
): void {
  qc.invalidateQueries({ queryKey: ['list', endpoint] });
  if (extra && extra !== endpoint) {
    qc.invalidateQueries({ queryKey: ['list', extra] });
  }
}

export function useCreateEntity<T>(endpoint: string, extraListEndpoint?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post<T>(endpoint, body),
    onSuccess: () => invalidateLists(qc, endpoint, extraListEndpoint),
  });
}

export function useUpdateEntity<T>(endpoint: string, extraListEndpoint?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api.put<T>(`${endpoint}/${id}`, body),
    onSuccess: () => invalidateLists(qc, endpoint, extraListEndpoint),
  });
}

export function useDeleteEntity(endpoint: string, extraListEndpoint?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: string }>(`${endpoint}/${id}`),
    onSuccess: () => invalidateLists(qc, endpoint, extraListEndpoint),
  });
}
